import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
  // Let AuthContext handle it
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  getMe: () => api.get("/auth/me"),
  updateProfile: (data) => api.put("/auth/profile", data),
  consent: () => api.post("/auth/consent"),
};

// Clients
export const clientsAPI = {
  list: (params) => api.get("/clients", { params }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post("/clients", data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Projects
export const projectsAPI = {
  list: (params) => api.get("/projects", { params }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post("/projects", data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
};

// Tasks
export const tasksAPI = {
  list: (params) => api.get("/tasks", { params }),
  get: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post("/tasks", data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
};

// Time Entries
export const timeEntriesAPI = {
  list: (params) => api.get("/time-entries", { params }),
  create: (data) => api.post("/time-entries", data),
  update: (id, data) => api.put(`/time-entries/${id}`, data),
  delete: (id) => api.delete(`/time-entries/${id}`),
};

// Timer
export const timerAPI = {
  start: (data) => api.post("/timer/start", data),
  stop: (clientId) => api.post("/timer/stop", null, { params: clientId ? { client_id: clientId } : undefined }),
  getActive: () => api.get("/timer/active"),
  discard: (clientId) => api.delete("/timer/discard", { params: clientId ? { client_id: clientId } : undefined }),
};

// Quotes
export const quotesAPI = {
  list: (params) => api.get("/quotes", { params }),
  get: (id) => api.get(`/quotes/${id}`),
  create: (data) => api.post("/quotes", data),
  update: (id, data) => api.put(`/quotes/${id}`, data),
  delete: (id) => api.delete(`/quotes/${id}`),
};

// Invoices
export const invoicesAPI = {
  list: (params) => api.get("/invoices", { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post("/invoices", data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
};

// Payments
export const paymentsAPI = {
  list: (params) => api.get("/payments", { params }),
  create: (data) => api.post("/payments", data),
  delete: (id) => api.delete(`/payments/${id}`),
};

// Client Links
export const clientLinksAPI = {
  list: (params) => api.get("/client-links", { params }),
  create: (data) => api.post("/client-links", data),
  update: (id, data) => api.put(`/client-links/${id}`, data),
  delete: (id) => api.delete(`/client-links/${id}`),
};

// Contacts
export const contactsAPI = {
  list: (params) => api.get("/contacts", { params }),
  create: (data) => api.post("/contacts", data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
};

// Suppliers
export const suppliersAPI = {
  list: () => api.get("/suppliers"),
  create: (data) => api.post("/suppliers", data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

// Leads
export const leadsAPI = {
  list: (params) => api.get("/leads", { params }),
  get: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post("/leads", data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
};

// Training Modules
export const trainingModulesAPI = {
  list: (params) => api.get("/training-modules", { params }),
  create: (data) => api.post("/training-modules", data),
  update: (id, data) => api.put(`/training-modules/${id}`, data),
  delete: (id) => api.delete(`/training-modules/${id}`),
};

// Recurring Invoices
export const recurringInvoicesAPI = {
  list: (params) => api.get("/recurring-invoices", { params }),
  get: (id) => api.get(`/recurring-invoices/${id}`),
  create: (data) => api.post("/recurring-invoices", data),
  update: (id, data) => api.put(`/recurring-invoices/${id}`, data),
  delete: (id) => api.delete(`/recurring-invoices/${id}`),
  generate: (id) => api.post(`/recurring-invoices/${id}/generate`),
};

// Subscriptions
export const subscriptionAPI = {
  getStatus: () => api.get("/subscription/status"),
  getConfig: () => api.get("/subscription/config"),
  activate: (data) => api.post("/subscription/activate", data),
  cancel: () => api.post("/subscription/cancel"),
};

// Admin
export const adminAPI = {
  getSubscribers: () => api.get("/admin/subscribers"),
  checkAccess: () => api.get("/admin/check"),
  deleteSubscriber: (id) => api.delete(`/admin/subscribers/${id}`),
  createSubscriber: (data) => api.post(`/admin/subscribers`, data),
  updateSubscriber: (id, data) => api.put(`/admin/subscribers/${id}`, data),
  
  // Affiliates
  listAffiliates: () => api.get("/admin/affiliates"),
  getAffiliate: (id) => api.get(`/admin/affiliates/${id}`),
  createAffiliate: (data) => api.post("/admin/affiliates", data),
  updateAffiliate: (id, data) => api.put(`/admin/affiliates/${id}`, data),
  addCommission: (affiliateId, amount) => api.post(`/admin/affiliates/${affiliateId}/commissions`, null, { params: { amount } }),
  markCommissionPaid: (affiliateId, commissionId) => api.put(`/admin/affiliates/${affiliateId}/commissions/${commissionId}/mark-paid`),
  deleteAffiliate: (id) => api.delete(`/admin/affiliates/${id}`),
  getReferrals: (affiliateId) => api.get(`/admin/affiliates/${affiliateId}/referrals`),
  getReferralStats: (affiliateId, startDate, endDate) => api.get(`/admin/affiliates/${affiliateId}/referral-stats`, { params: { start_date: startDate, end_date: endDate } }),
  generatePaymentReceipt: (affiliateId, data) => api.post(`/admin/affiliates/${affiliateId}/payment-receipt`, data),

  // Reminders
  getReminderConfig: () => api.get("/admin/reminder-config"),
  updateReminderConfig: (data) => api.put("/admin/reminder-config", data),
  sendTrialReminders: () => api.post("/admin/send-trial-reminders"),
  getReminderLogs: () => api.get("/admin/reminder-logs"),
  previewTrialReminders: () => api.get("/admin/trial-reminders/preview"),

  // Announcements
  listAnnouncements: () => api.get("/admin/announcements"),
  createAnnouncement: (data) => api.post("/admin/announcements", data),
  updateAnnouncement: (id, data) => api.put(`/admin/announcements/${id}`, data),
  deleteAnnouncement: (id) => api.delete(`/admin/announcements/${id}`),

  // Admin Exports
  exportUsers: (params) => api.get("/admin/export/users", { params, responseType: "blob" }),
  exportAffiliates: (params) => api.get("/admin/export/affiliates", { params, responseType: "blob" }),
  exportCommissionLedger: (params) => api.get("/admin/export/commission-ledger", { params, responseType: "blob" }),
  exportReferredAccounts: (params) => api.get("/admin/export/referred-accounts", { params, responseType: "blob" }),
  exportAffiliateFullReport: (affiliateId) => api.get(`/admin/export/affiliates/${affiliateId}/full`, { responseType: "blob" }),

  // Activity
  getUserActivity: (userId, page = 1, limit = 50) => api.get(`/admin/users/${userId}/activity`, { params: { page, limit } }),
};

// File Upload
export const uploadAPI = {
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/upload/logo", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get("/dashboard/stats"),
};

// Reminders
export const remindersAPI = {
  list: () => api.get("/reminders"),
};

// Calendar
export const calendarAPI = {
  getEvents: (params) => api.get("/calendar/events", { params }),
};

// Announcements (user-facing)
export const announcementsAPI = {
  getActive: () => api.get("/announcements/active"),
  dismiss: (id) => api.post(`/announcements/${id}/dismiss`),
};

// Rate Resolution
export const rateAPI = {
  getResolved: (clientId, params) => api.get(`/rate/${clientId}`, { params }),
};

// Financials
export const financialsAPI = {
  // Income
  getIncome: (params) => api.get("/financials/income", { params }),
  getIncomeById: (id) => api.get(`/financials/income/${id}`),
  createIncome: (data) => api.post("/financials/income", data),
  updateIncome: (id, data) => api.put(`/financials/income/${id}`, data),
  deleteIncome: (id) => api.delete(`/financials/income/${id}`),
  
  // Expenses
  getExpenses: (params) => api.get("/financials/expenses", { params }),
  getExpenseById: (id) => api.get(`/financials/expenses/${id}`),
  createExpense: (data) => api.post("/financials/expenses", data),
  updateExpense: (id, data) => api.put(`/financials/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/financials/expenses/${id}`),
  uploadReceipt: (expenseId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/financials/expenses/${expenseId}/receipt`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  
  // Assets
  getAssets: () => api.get("/financials/assets"),
  createAsset: (data) => api.post("/financials/assets", data),
  updateAsset: (id, data) => api.put(`/financials/assets/${id}`, data),
  deleteAsset: (id) => api.delete(`/financials/assets/${id}`),
  
  // Tax Settings
  getTaxSettings: () => api.get("/financials/tax-settings"),
  updateTaxSettings: (data) => api.put("/financials/tax-settings", data),
  
  // Tax Summary
  getTaxSummary: (params) => api.get("/financials/tax-summary", { params }),
  
  // Dashboard
  getDashboard: () => api.get("/financials/dashboard"),
  
  // Import
  importIncome: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/financials/import/income", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  importExpenses: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/financials/import/expenses", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Export
export const exportAPI = {
  clients: () => `${API_BASE}/export/clients`,
  projects: (params) => {
    const query = new URLSearchParams(params).toString();
    return `${API_BASE}/export/projects${query ? `?${query}` : ""}`;
  },
  tasks: (params) => {
    const query = new URLSearchParams(params).toString();
    return `${API_BASE}/export/tasks${query ? `?${query}` : ""}`;
  },
  projectTracker: (params) => {
    const query = new URLSearchParams(params).toString();
    return `${API_BASE}/export/project-tracker${query ? `?${query}` : ""}`;
  },
  timeEntries: (params) => {
    const query = new URLSearchParams(params).toString();
    return `${API_BASE}/export/time-entries${query ? `?${query}` : ""}`;
  },
  invoices: (params) => {
    const query = new URLSearchParams(params).toString();
    return `${API_BASE}/export/invoices${query ? `?${query}` : ""}`;
  },
  quotes: (params) => {
    const query = new URLSearchParams(params).toString();
    return `${API_BASE}/export/quotes${query ? `?${query}` : ""}`;
  },
  payments: (params) => {
    const query = new URLSearchParams(params).toString();
    return `${API_BASE}/export/payments${query ? `?${query}` : ""}`;
  },
  leads: (params) => {
    const query = new URLSearchParams(params).toString();
    return `${API_BASE}/export/leads${query ? `?${query}` : ""}`;
  },
  suppliers: () => `${API_BASE}/export/suppliers`,
  contacts: (params) => {
    const query = new URLSearchParams(params).toString();
    return `${API_BASE}/export/contacts${query ? `?${query}` : ""}`;
  },
  // Financial exports
  financialIncome: (params) => {
    const query = new URLSearchParams(params).toString();
    return `${API_BASE}/financials/export/income${query ? `?${query}` : ""}`;
  },
  financialExpenses: (params) => {
    const query = new URLSearchParams(params).toString();
    return `${API_BASE}/financials/export/expenses${query ? `?${query}` : ""}`;
  },
  financialAssets: () => `${API_BASE}/financials/export/assets`,
  budgetHistory: () => `${API_BASE}/export/budget-history`,
};

// Analytics
export const analyticsAPI = {
  getSummary: () => api.get("/analytics/summary"),
  getReflections: () => api.get("/analytics/reflections"),
  saveReflection: (data) => api.post("/analytics/reflections", data),
  getSuggestions: () => api.get("/analytics/suggestions"),
  applySuggestions: (data) => api.post("/analytics/suggestions/apply", data),
  getSavings: () => api.get("/analytics/savings"),
  getCashSnapshot: () => api.get("/analytics/cash-snapshot"),
  getCashPressure: () => api.get("/analytics/cash-pressure"),
  getAccountMovement: () => api.get("/analytics/account-movement"),
  getProjectedCoverage: (month, simulateAmounts) =>
    api.get("/analytics/projected-coverage", {
      params: {
        ...(month ? { month } : {}),
        ...(simulateAmounts ? { simulate_amounts: simulateAmounts } : {}),
      },
    }),
};

// Income Allocations
export const incomeAllocationsAPI = {
  list: (params) => api.get("/income-allocations", { params }),
  create: (data) => api.post("/income-allocations", data),
  update: (id, data) => api.put(`/income-allocations/${id}`, data),
  delete: (id) => api.delete(`/income-allocations/${id}`),
};

// Budget Planner
export const budgetPlannerAPI = {
  // Plans
  listPlans: (params) => api.get("/budget/plans", { params }),
  getPlanByMonth: (month, year) =>
    api.get("/budget/plans/by-month", { params: { month, year } }),
  createPlan: (data) => api.post("/budget/plans", data),
  updatePlan: (id, data) => api.put(`/budget/plans/${id}`, data),
  lockPlan: (id) => api.post(`/budget/plans/${id}/lock`),
  deletePlan: (id) => api.delete(`/budget/plans/${id}`),
  adminDeletePlan: (id) => api.delete(`/budget/plans/${id}/admin-delete`),
  migrateDefaults: () => api.post("/budget/plans/migrate-defaults"),
  getUpcomingObligations: () => api.get("/budget/upcoming-obligations"),
  // Categories
  getCategories: () => api.get("/budget/categories"),
  getBuiltinCategories: () => api.get("/budget/categories/builtin"),
  createCategory: (data) => api.post("/budget/categories", data),
  updateCategory: (id, data) => api.put(`/budget/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/budget/categories/${id}`),
};

// Accounts & Balances (formerly Savings Tracker)
export const savingsAPI = {
  list: () => api.get("/savings"),
  create: (data) => api.post("/savings", data),
  update: (id, data) => api.put(`/savings/${id}`, data),
  delete: (id) => api.delete(`/savings/${id}`),
  getTransactions: (id) => api.get(`/savings/${id}/transactions`),
  adjustBalance: (id, data) => api.post(`/savings/${id}/adjust`, data),
  getBalancePreview: (id, direction, amount) =>
    api.get(`/savings/${id}/balance-preview`, { params: { direction, amount } }),
};

// Liability Accounts (credit cards and loans)
export const liabilityAccountsAPI = {
  list: () => api.get("/liability-accounts"),
  create: (data) => api.post("/liability-accounts", data),
  update: (id, data) => api.put(`/liability-accounts/${id}`, data),
  delete: (id) => api.delete(`/liability-accounts/${id}`),
};

// Installment Schedules
export const installmentSchedulesAPI = {
  list: (params) => api.get("/installment-schedules", { params }),
  get: (id) => api.get(`/installment-schedules/${id}`),
  create: (data) => api.post("/installment-schedules", data),
  update: (id, data) => api.put(`/installment-schedules/${id}`, data),
  markPayment: (id, month, params) =>
    api.patch(`/installment-schedules/${id}/payments/${month}`, null, { params }),
  delete: (id) => api.delete(`/installment-schedules/${id}`),
};

export default api;